import type { ReactElement } from 'react';
import React from 'react';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';

import Typography from 'src/components/ui/Typography';

import { styledComponent, styledTag } from 'src/utils/styled';

const StyledOverflow = styledComponent(Box)`
    overflow: hidden;
`;

type StyledCardActionAreaProps = {
    $padding?: string;
    $minHeight?: string;
    $cursorPointer?: boolean;
    component?: string;
};
const StyledCardActionArea = styledComponent(CardActionArea)<StyledCardActionAreaProps>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    ${({ $padding, $minHeight }) =>
        `
        ${$padding ? `padding: ${$padding};` : ''}
        ${$minHeight ? `min-height: ${$minHeight};` : ''}
    `}
    ${({ $cursorPointer }) => ($cursorPointer ? 'cursor: pointer;' : 'cursor: auto;')}
`;

const RightArea = styledTag('div')`
    margin-left: auto;
`;

interface Props {
    keyIdentifier: string;
    firstSubtitle?: string | 'activity' | null; // use null to don't display first subtitle or a custom string to dispaly it as sunbtitle
    secondSubtitle?: string | 'commonRelationsCount' | null;
    rightArea?: React.ReactElement;
    padding?: string;
    minHeight?: string;
    onClick?: () => void;
    dataTargetId?: string;
    backgroundColor?: string;
    avatar: ReactElement<unknown, string>;
    name: string;
    cursorPointer?: boolean;
    noWrapOnTitle?: boolean;
    noWrapOnSubtitles?: boolean;
    disableRipple?: boolean;
}

const SimpleHorizontalCard: React.FC<Props> = ({
    keyIdentifier,
    firstSubtitle,
    secondSubtitle,
    rightArea,
    padding,
    minHeight,
    onClick,
    dataTargetId,
    backgroundColor,
    avatar,
    name,
    cursorPointer = true,
    noWrapOnTitle = true,
    noWrapOnSubtitles = true,
    disableRipple,
}) => (
    <Card key={keyIdentifier} elevation={0} data-target-id={dataTargetId} sx={{ backgroundColor }}>
        <StyledCardActionArea
            component="div"
            $padding={padding}
            $minHeight={minHeight}
            $cursorPointer={cursorPointer}
            onClick={onClick}
            disableRipple={disableRipple}
        >
            {avatar}
            <StyledOverflow pl={2} className="simple-horizontal-card-text">
                <Typography variant="h5" noWrap={noWrapOnTitle}>
                    {name}
                </Typography>
                {firstSubtitle ? (
                    <Box display="flex">
                        <Typography variant="subtitle2" noWrap={noWrapOnSubtitles}>
                            {firstSubtitle}
                        </Typography>
                    </Box>
                ) : null}
                {secondSubtitle ? (
                    <Typography variant="subtitle2" noWrap={noWrapOnSubtitles}>
                        {secondSubtitle}
                    </Typography>
                ) : null}
            </StyledOverflow>
            {rightArea && <RightArea>{rightArea}</RightArea>}
        </StyledCardActionArea>
    </Card>
);

export default SimpleHorizontalCard;
